import { FC, useState, useCallback, useRef } from "react";
import styled from "styled-components";
import { getPath } from "./Image";
import { Transition } from "react-transition-group";
import { Speech } from "./Paragraph";
import { useLocale } from "hooks";

const fadeIn = {
  transitionDuration: 500,
  defaultTransitionStyle: {
    transition: "opacity 500ms ease-in-out",
  },
  transitionStyles: {
    entering: { opacity: 1, pointerEvents: "auto" },
    entered: { opacity: 1, pointerEvents: "auto" },
    exiting: { opacity: 0, pointerEvents: "none" },
    exited: { opacity: 0, pointerEvents: "none" },
  },
};

export const Yugi: FC<React.PropsWithChildren<unknown>> = () => {
  const sound = false;
  const [y, setY] = useState("0");
  const [x, setX] = useState("0");
  const [giraffe, setGiraffe] = useState(true);
  const [poppedBalloons, setPoppedBalloons] = useState(0);
  const [yellow, setYellow] = useState(true);
  const [green, setGreen] = useState(true);
  const [blue, setBlue] = useState(true);
  const [purple, setPurple] = useState(true);
  const [rope, setRope] = useState(true);
  const [message, setMessage] = useState("We’re taking insurance to new heights!");
  const { locale } = useLocale();
  const nodeRef = useRef(null);

  const popYellow = useCallback(() => {
    setYellow(false);
  }, [setYellow]);

  const popGreen = useCallback(() => {
    setGreen(false);
  }, [setGreen]);

  const popBlue = useCallback(() => {
    setBlue(false);
  }, [setBlue]);

  const popPurple = useCallback(() => {
    setPurple(false);
  }, [setPurple]);

  const incrementPoppedBalloons = useCallback(() => {
    setPoppedBalloons((poppedBalloons) => poppedBalloons + 1);
  }, [setPoppedBalloons]);

  const pop = () => {
    setMessage("");
    switch (poppedBalloons) {
      case 0:
        setY(Number(y.match(/\d+/)[0]) + 0.5 + "rem");
        break;
      case 1:
        setY(Number(y.match(/\d+/)[0]) + 2.5 + "rem");
        break;
      case 2:
        setY(Number(y.match(/\d+/)[0]) + 5 + "rem");
        break;
      case 3:
      default:
        setY("150vh");
        setTimeout(() => {
          setGiraffe(false);
          setX("10rem");
          setY("0");
        }, 500);
        setTimeout(() => {
          setX("0");
          setY("0");
          setPoppedBalloons(0);
          setGiraffe(true);
          setYellow(true);
          setGreen(true);
          setBlue(true);
          setPurple(true);
          setRope(true);
          setMessage("I'm protected!");
        }, 2000);
        break;
    }

    if (sound) {
      const audio = new Audio(getPath("/sound/pop.mp3"));
      audio.volume = 0.05;
      audio.play();
    }
  };

  return (
    // @ts-ignore: <Transition> error with @types/react 18.0.3
    <Transition in={giraffe} timeout={fadeIn.transitionDuration} nodeRef={nodeRef}>
      {(state) => (
        <div
          ref={nodeRef}
          style={{
            ...fadeIn.defaultTransitionStyle,
            ...fadeIn.transitionStyles[state],
          }}
        >
          <Screen>
            <OuterContainer style={{ transform: `translate(${x || 0}, ${y || 0})` }}>
              <InnerContainer>
                {giraffe && (
                  <Wrapper>
                    <Giraffe />
                    {locale === "za" && (
                      <ScarfWrapper>
                        <SAScarf />
                      </ScarfWrapper>
                    )}
                    <StyledSpeech textAlign="right">{message}</StyledSpeech>
                  </Wrapper>
                )}
                {yellow && (
                  <Wrapper>
                    <YellowBalloon />
                  </Wrapper>
                )}
                {green && (
                  <Wrapper>
                    <GreenBalloon />
                  </Wrapper>
                )}
                {blue && (
                  <Wrapper>
                    <BlueBalloon />
                  </Wrapper>
                )}
                {purple && (
                  <Wrapper>
                    <PurpleBalloon />
                  </Wrapper>
                )}
                {rope && (
                  <Wrapper>
                    <Rope />
                  </Wrapper>
                )}
                {yellow && (
                  <YellowClickZone
                    onClick={() => {
                      popYellow();
                      incrementPoppedBalloons();
                      pop();
                    }}
                  />
                )}
                {green && (
                  <GreenClickZone
                    onClick={() => {
                      popGreen();
                      incrementPoppedBalloons();
                      pop();
                    }}
                  />
                )}
                {blue && (
                  <BlueClickZone
                    onClick={() => {
                      popBlue();
                      incrementPoppedBalloons();
                      pop();
                    }}
                  />
                )}
                {purple && (
                  <PurpleClickZone
                    onClick={() => {
                      popPurple();
                      incrementPoppedBalloons();
                      pop();
                    }}
                  />
                )}
              </InnerContainer>
            </OuterContainer>
          </Screen>
        </div>
      )}
    </Transition>
  );
};

const OuterContainer = styled.div`
  width: 22rem;
  position: relative;
  transition-property: transform;
  transition-duration: 0.5s;
  transition-timing-function: ease-in-out;
`;

const ClickZone = styled.div`
  background: none;
  border-radius: 50%;
  position: absolute;
  cursor: pointer;
`;

const YellowClickZone = styled(ClickZone)`
  width: 7rem;
  height: 7rem;
  top: 0;
  right: 5.9rem;
`;

const GreenClickZone = styled(ClickZone)`
  width: 6.6rem;
  height: 6.6rem;
  top: 7.2rem;
  right: 6.6rem;
`;

const BlueClickZone = styled(ClickZone)`
  width: 7.4rem;
  height: 7.4rem;
  top: 2.8rem;
  right: 2.6rem;
`;

const PurpleClickZone = styled(ClickZone)`
  width: 9rem;
  height: 9rem;
  right: 9.4rem;
  top: 1.5rem;
`;

const Screen = styled.div`
  height: 100vh;
  width: 100%;
  padding-right: 15rem;
  padding-bottom: 15rem;
  transform: translate(15rem, -2.5rem);
`;

const InnerContainer = styled.div`
  width: 22rem;
  position: relative;
  animation: bob 3s linear infinite alternate;

  @keyframes bob {
    0% {
      transform: translateY(0.5rem);
    }
    100% {
      transform: translateY(1.5rem);
    }
  }
`;

const Wrapper = styled.div`
  width: 22rem;
  position: absolute;
  top: 0;
`;

const ScarfWrapper = styled.div`
  position: absolute;
  top: 16.2rem;
  right: 12.65rem;
`;

const StyledSpeech = styled(Speech)`
  width: 7.6rem;
  position: absolute;
  top: 50%;
  left: -20%;

  @media only screen and (max-width: 40rem) {
    width: 7.6rem;
    top: 62%;
    left: -16%;
  }
`;

const Giraffe = () => (
  <svg
    role="img"
    aria-labelledby="YuLife-Insurance-inspires-header-image"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 352 640"
  >
    <title id="YuLife-Insurance-inspires-header-image">
      Yugi the cartoon giraffe held aloft by balloons taking insurance to new heights
    </title>
    <path
      fill="#c98d2b"
      d="M276 624.47l-4.82-9.55a3.59 3.59 0 00-2.28-1.78 4.7 4.7 0 00-1.4-.13L243 617a3.33 3.33 0 00-2.29 1.4 4.29 4.29 0 00-.64 2.16l.77 16.18a3.43 3.43 0 004.69 3l28.57-10.57a3.38 3.38 0 001.9-4.7zM351.65 596.58l-4.82-9.58a3.14 3.14 0 00-2-1.79 3.2 3.2 0 00-1.53-.13l-24.5 3.95a3.44 3.44 0 00-2.16 1.28 3.53 3.53 0 00-.76 2.29l.63 16.17a3.45 3.45 0 004.7 3.06l28.57-10.57a3.42 3.42 0 001.87-4.68z"
    />
    <path
      d="M41.74 191.43l6.22-4-2-3.44s-1.27-2.29-1.9-3.31c-1.91-3.69-2.54-7-1.91-10.19l1.65-7.89v-.13a18.76 18.76 0 000-3.82l-.13-.89a2.73 2.73 0 01.13-1l.89-2.3a5 5 0 00-.51-4.45 5.41 5.41 0 00-3.81-2.42l-1.9-.26a5.09 5.09 0 00-4.19 1.53 5 5 0 00-1.4 4.33l.38 2.55a3 3 0 01-.12 1l-.26.89a19.37 19.37 0 00-.76 3.69l-.38 8.92-7.62-4.21H24c-1.15-.5-2.42-.89-3.56-1.27l-.89-.25a2.17 2.17 0 01-.89-.51l-1.9-1.53a5 5 0 00-4.44-.89 4.37 4.37 0 00-3.72 2.72l-.76 1.78a5.15 5.15 0 003.68 7.14l2.54.51a1.13 1.13 0 01.89.38l.76.51a17.58 17.58 0 003.3 1.91l7.75 2.67c3 1 5.59 3.31 7.74 6.88.64 1 1.91 3.31 1.91 3.31l2.41 4.33c.51-.38 1-.89 1.52-1.27s.89-.64 1.4-1.02z"
      fill="#7a4412"
    />
    <path
      d="M336.16 452.91l-15.1-41.14a26.94 26.94 0 00-34.67-16l-20.69 7.65c-24.63 9-67.67-7.65-79.48-25-9.9-14.52-81.13-124.82-104.49-161l-5-13.5c5.33-2.16 10.67-5 13.21-8.53 4.19-5.86 5.2-11.08 5.07-18.47s-2.53-14.26-3.3-21.52c-5 1.91-10.53 3.94-15 5.73-5.2 2.16-10.16 5.47-12.82 10.44A30.51 30.51 0 0060.15 183a14.71 14.71 0 00-8.89 2.43l-3.55 2.29-6.22 3.95-1.49 1.16a14.15 14.15 0 00-1.52 1.28c-9 7.38-21.2 17.7-25.77 22.67-9.78 10.57-17.52 26.36-9 38.59 0 .12.13.12.13.12s.13.26.25.26a33 33 0 0037.2 10.57c.13 0 .51-.25.64-.25l12.69-4.72 4.7-1.65 112.81 183.53c23.23 63.3 65 167.23 68.3 175.51a3.13 3.13 0 012.29-1.4l24.5-4a2.59 2.59 0 011.4.13l-27.55-102.86c-1.78-6.24.76-10.32 5.59-12.1l18.28-6.75a9.45 9.45 0 0112.06 5.6l39.36 93.23a3.47 3.47 0 012.16-1.27l24.5-4a3 3 0 011.52.13l-29.83-104.39c-1.4-4.71-2.92-9.3-4.57-14l-16.63-45.47a26.33 26.33 0 00-9.51-12.37l6.22-2.29a15 15 0 0119.05 8.79L324.36 457a9.06 9.06 0 00-.38 6.87 8.67 8.67 0 0012.82 4.33 8.77 8.77 0 003.93-8.53 7.63 7.63 0 00-4.57-6.76z"
      fill="#ffdd08"
    />
    <path
      d="M41.36 216.14a5.64 5.64 0 014.44-2 5.57 5.57 0 013.94 1.66 1.56 1.56 0 002.67-1.28v-.12a7.68 7.68 0 00-5.59-6.12 7.6 7.6 0 00-9.52 7 10.27 10.27 0 00.12 1.91 1.47 1.47 0 002.8.38 13.23 13.23 0 001.14-1.43z"
      fill="#3e3e3d"
    />
    <path
      d="M59.52 259.44s14.21-5.6 19.8-13c0 0-2.41 13.63-14.47 21.65 0-.09-5.33-8.65-5.33-8.65z"
      fill="#e1c31e"
    />
    <path
      fill="#c98d2b"
      d="M130.49 319.3a4 4 0 00-2.42 5.23 4.11 4.11 0 005.21 2.42 4.07 4.07 0 00-2.79-7.65zM174.92 373.31a4.11 4.11 0 005.21 2.42 4 4 0 002.54-2.81c-.89-1.4-2-3.05-3.18-5a4.21 4.21 0 00-2.15.12 4 4 0 00-2.42 5.27zM157.53 345.92a5.35 5.35 0 10-3.18 6.88 5.31 5.31 0 003.18-6.88zM143.82 371a5.36 5.36 0 106.85 3.19 5.34 5.34 0 00-6.85-3.19zM120.71 295.61a6.07 6.07 0 007.75 3.57 5.91 5.91 0 003.68-4.07l-4.95-7.65a7.69 7.69 0 00-2.93.26 6.34 6.34 0 00-3.55 7.89zM129 350.64a7.94 7.94 0 00-10.15-4.72 8.18 8.18 0 00-4.07 3.31l7.37 12a6.73 6.73 0 002.15-.51 7.69 7.69 0 004.7-10.08zM104.84 320.07a6.54 6.54 0 00-8.25-3.82 6.68 6.68 0 00-1.65.89l6.73 10.86a6.33 6.33 0 003.17-7.93zM194.35 417.12a8.32 8.32 0 00-11.3 4.59 8.43 8.43 0 004.57 10.69l12.82 5.86a2.33 2.33 0 003.05-1.14l5.84-12.87a8.3 8.3 0 10-15-7.13zM223.55 421.71a4.57 4.57 0 00-2.67 5.85 4.46 4.46 0 108.38-3.05 4.28 4.28 0 00-5.71-2.8zM264.18 407.19a4.58 4.58 0 00-2.67 5.86 4.46 4.46 0 108.38-3.06 4.38 4.38 0 00-5.71-2.8zM247.93 422a7.93 7.93 0 1010.15 4.71 7.84 7.84 0 00-10.15-4.71zM238.79 412a7.69 7.69 0 005.07-6.63 97.88 97.88 0 01-15.61-2.67 8.06 8.06 0 00.25 4.58 8.15 8.15 0 0010.29 4.72zM205.65 401.58a5.31 5.31 0 006.85 3.19 5.45 5.45 0 003.43-6.24c-2.66-1-5.33-2.3-7.87-3.57a5.43 5.43 0 00-2.41 6.62z"
    />
    <path
      fill="#C78800"
      d="M324.246 456.658s-5.971 17.606-1.138 19.484c4.832 1.877 6.627-7.76 6.627-7.76s-.021 16.83 6.206 16.184c6.227-.647 2.007-17.045 2.007-17.045s7.143 8.264 10.282 3.727c3.139-4.537-12.101-18.41-12.101-18.41l-11.883 3.82z"
    />
  </svg>
);

const YellowBalloon = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 -3 352 640">
    <path
      d="M195.68 117.63L227.87 399"
      fill="none"
      stroke="#bebcbc"
      strokeMiterlimit="10"
      strokeWidth="1.4"
    />
    <path
      d="M143.31 52.22a52.37 52.37 0 1154.21 52.86l6.48 9.17a2.19 2.19 0 01-1.78 3.44l-13.71-.13a2.18 2.18 0 01-1.78-3.44l6.6-9.17a52.43 52.43 0 01-50.02-52.73z"
      fill="#ffd600"
    />
  </svg>
);

const PurpleBalloon = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 640">
    <path
      d="M61.93 119a68.76 68.76 0 1189.76 42.28l11.93 8.67a2.92 2.92 0 01-.76 5.09l-17.14 5.73a2.88 2.88 0 01-3.68-3.56l4.32-14.21a68.74 68.74 0 01-84.43-44z"
      fill="#956aff"
    />
    <path
      d="M154.74 177.55l73.63 225.18"
      fill="none"
      stroke="#bebcbc"
      strokeMiterlimit="10"
      strokeWidth="1.4"
    />
  </svg>
);

const BlueBalloon = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 640">
    <path
      d="M185.59 102.66a59.42 59.42 0 1161.32 60l7.24 10.44a2.51 2.51 0 01-2 3.95H236.5a2.51 2.51 0 01-2-3.95l7.5-10.33a59.63 59.63 0 01-56.41-60.11z"
      fill="#00c0f3"
    />
    <path
      d="M244.5 177.17l-16.13 225.56"
      fill="none"
      stroke="#bebcbc"
      strokeMiterlimit="10"
      strokeWidth="1.4"
    />
  </svg>
);

const Rope = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 640">
    <path
      d="M228.37 402.73a675.73 675.73 0 0128.44 91.7"
      fill="none"
      stroke="#bebcbc"
      strokeMiterlimit="10"
      strokeWidth="1.4"
    />
  </svg>
);

const GreenBalloon = () => (
  <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 352 640">
    <path
      d="M139.88 170.67a54 54 0 1163.74 45l8 8.41a2.29 2.29 0 01-1.27 3.82l-14.1 2.1a2.19 2.19 0 01-2.28-3.18l5.2-10.32a54.37 54.37 0 01-59.29-45.83z"
      fill="#00ed9d"
    />
    <path
      d="M228.12 400.69a6.47 6.47 0 00.25 2l-25.26-173.81"
      fill="none"
      stroke="#bebcbc"
      strokeMiterlimit="10"
      strokeWidth="1.4"
    />
  </svg>
);
const SAScarf = () => (
  <svg width="82" height="158" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m31.1 88.4-5.4-5 .3-1.1 3.4-10.5c-.8-3.4 2.1-2.6 2.1-2.6v.6a1418.9 1418.9 0 0 0-.4 18.6Z"
      fill="#F40600"
    />
    <path d="M31.1 91.4c-2-1.3-4.7-3.9-6.2-5.3l.8-2.7 5.4 5v3Z" fill="#DEF8FD" />
    <path
      d="M31 103.6c-3.2-1.5-7-3-9-3.7h.1c.3-3 1.2-8.1 2.8-13.7 1.5 1.3 4.2 3.9 6.2 5.2v12.2Z"
      fill="#008500"
    />
    <path d="M31 107c-2-1-6.9-3.1-9-4v-3.2c2 .8 5.8 2.3 9 3.8v3.5Z" fill="#DEF8FD" />
    <path
      d="M30.5 129.4c-1.2-.4-1.4-2-.6-5.2.8-3.1-.4-6-.5-6.2 0 1.2-.7 3.8-1.5 7-.8 3.3.6 3.8.1 4.8-.4 1-1 .4-1 .4-2.4-3.8-.4-7.7-.2-11.5.3-3.8-.3-3.2-.3-3.2a6.5 6.5 0 0 0-1.2 5.7c.4 1.7-.3 1.7-.9 1.6-.5-.2-1.9-2.9-.5-7.8 1.3-4.8.3-6.3.3-6.4 0 1.4-.9 5-1.9 8.4-1 3.5-.2 6.1.2 8 .5 2-.3 2.2-1.4 2-1.1-.2-2.4-5.6-1-11 1.3-5.5 1.3-7.9 1.3-7.9s.4-2.8.5-5.2c2.2 1 7 3 9.2 4.2 0 5.6.2 10 .6 11v.1l.4 1.2c.8 3.6-.6 6.4-.5 7.8 0 1.4 0 2.6-1.1 2.2Z"
      fill="#1834CE"
    />
    <path
      d="M51.9 76.2a12 12 0 0 1-5.8-3.4c-3.3-3.3-7.8-6.5-14.5-3v-.6c3.5-9 17.7-3.3 17.7-3.3 1.2 1.7 2.1 7.3 2.6 10.3Z"
      fill="#3955F5"
    />
    <path
      d="M52.5 82.1c-1.7 0-4.5-.6-7.8-3.3-5.1-4-8.2-7.4-13.1-9 6.7-3.5 11.2-.4 14.5 3 2 2.1 4.4 3 5.8 3.4.3 1.9.5 3.9.6 6Z"
      fill="#fff"
    />
    <path
      d="M52.2 94.2c-1.3.7-3.8.8-8.2-2.4-7-5-9-8.2-13.9-10a196.9 196.9 0 0 1 1.5-12c4.9 1.6 8 5 13 9a13 13 0 0 0 7.9 3.3c.1 4.1 0 8.4-.3 12.1Z"
      fill="#00A705"
    />
    <path
      d="M51.6 106.2a59.5 59.5 0 0 0-15.8 19c-.5-1.4-1.3-2.8-2.3-4.1-.5-.6-1-1.2-1.3-2C39.4 107 49 99.9 49.3 99.6c-.2 0-7.3-.2-12.6-5-3.5-3-5.8-4.8-7-5.6v-1.7l.4-5.4c4.8 1.8 7 5 14 10 4.3 3.2 6.8 3 8.1 2.4a102 102 0 0 1-.5 5.7c-.3 2.1-.3 4.2 0 6.3Z"
      fill="#FFB400"
    />
    <path
      d="M49.3 99.5c-.3.3-9.9 7.5-17 19.7l-.5-1a34.9 34.9 0 0 1-2.1-12 138.2 138.2 0 0 1-.1-16V89c1.3.9 3.6 2.6 7 5.6 5.4 4.8 12.5 5 12.7 5Z"
      fill="#352B28"
    />
    <path
      d="M42.4 145.5c-.6 1.9-.9 2.7-1.5 2.5-.6-.1-1-.6-.5-1.8.6-1.2.3-5.3-.2-6.1-.5-.9-.4-6.7-.6-7 0-.4-.4-.5-1.2 2.4-.8 3 .6 6.3.5 7.3 0 1-.2 1.5-1.4 1.2-1.2-.4-1.4-8.4-1-9.8.4-1.5.4-3.8.4-3.8 0-1-.2-3-1-5.2a59.4 59.4 0 0 1 15.7-19c.5 4.2 1.7 8.4 2.8 12.2-2 1.7-6 6-11.4 14.7-.1-.2-.2-.3-.4-.2-.5.2-1.2 2.6-1 4.6.2 2 1.5 6.2.8 8Z"
      fill="#00A705"
    />
    <path
      d="M46 147.8c-.5 1-2 1-1.9.3.2-.7.6-4.5-.2-6.7-.8-2-1-5.9-.9-6.6a3 3 0 0 0 0-1.7c5.4-8.8 9.4-13 11.4-14.8l.9 3c-2.7 3.1-7.2 10.3-8.5 12.6l-.1-.8c0-.7-1.2-.2-1.3 2.2-.1 2.3.4 3.7.7 5.1a25 25 0 0 1-.2 7.4Z"
      fill="#fff"
    />
    <path
      d="M56.9 145.9c-2.4-1.5-1.2-4.4-.7-7.1.6-2.7 0-6.7-.6-7.2-.6-.6-.7 0-.7 0a35 35 0 0 1-.8 11.1c-.5 1.9 0 2 .1 3.2.2 1-.5 1.5-1 1.3-.4-.3-.2-1.6-.6-2.2-.3-.6 0-5.2.5-7.5.7-2.3-.2-4-.3-4.3 0-.3-.5-.4-.5.2 0 .6.2 4.7-.4 6.8-.7 2-.2 3.4 0 4.4.2 1-.5 1-1 .8-.4-.3-1.2-2.8-.7-5 .5-2.3.3-5.4.2-6.2-.2-.7-.5-1-.9-1-.3.2-.3 1.4-.3 2.4s-.7 4.8-.7 6.4c0 1.6.3 3.5 1 4.7.8 1.2.4 1.6 0 1.6s-.7-.1-1.8-2a19 19 0 0 1-.8-8.4c.1-1.3 0-3-.2-4 1.4-2.3 5.9-9.4 8.6-12.7l.2.8a41 41 0 0 1 2 17.3c-1 2.1-.8 4.6-.3 5.6s-.3 1-.3 1Z"
      fill="#3955F5"
    />
    <path
      d="M60.6 36.9c-2.9 6-9.2 15.7-12.3 19.4-2.3-2.4-5-4.2-6-4.6 0 0-.1-3.9 6.9-12.7 7-8.8 7.7-14.8 5.8-18.6 0 0 5.4-.7 8 3.5 0 2.8 0 8-2.4 13Z"
      fill="#F40600"
    />
    <path
      d="M66.1 34.5A20 20 0 0 1 61.5 45l-11 14.4a12.1 12.1 0 0 0-2.2-3.1c3-3.7 9.4-13.4 12.3-19.4 2.3-5 2.5-10.2 2.3-13 .6 1 1 2.3 1.3 4 .4 3.5 1.2 5.4 2 6.6Z"
      fill="#fff"
    />
    <path
      d="M67.8 39.8c-.6 4.8-3.6 10.3-7 12.3-3.5 2-9.5 11-9.5 11 .1-1.3-.2-2.6-.8-3.7l11-14.4c3.5-4.7 4.4-8.3 4.6-10.5 1.1 1.8 2 2.5 1.7 5.3Z"
      fill="#008500"
    />
    <path d="M27 56.9c-1.3 1-2.6 2-3.5 3.3l-.2-.1s-4-4-.9-11.1l4.7 7.9Z" fill="#F40600" />
    <path
      d="M49.2 57.4a84 84 0 0 0-9.9 1.4 24.3 24.3 0 0 0-1-6.4l4-.7a20.3 20.3 0 0 1 7 5.7Z"
      fill="#008500"
    />
    <path
      d="M39.3 58.8A45.1 45.1 0 0 0 27 63.1a17.9 17.9 0 0 0-4.2 3c-.4-1.9 0-4.8 0-4.8l.7-1a13 13 0 0 1 3.6-3.4c3.5-2.4 8-3.7 11.2-4.5a30.2 30.2 0 0 1 1 6.4Z"
      fill="#00A705"
    />
    <path
      d="m51.3 63-.8.2a48 48 0 0 0-11.8.3c.5-1.5.7-3.1.6-4.7a85.2 85.2 0 0 1 10-1.4l1.2 2c.6 1.1 1 2.4.8 3.6Z"
      fill="#DEF8FD"
    />
    <path
      d="M51.3 63.2c-.3 3.2-1.7 4.6-5.2 5.8-2.2.8-5.2 1.5-9.3 2.5-4.4 1.2-6.8 2.6-8 3.9l-.5.6c-.5.8-1 2.6-1.1 2.8-.6 0 0 0 0 0-2 .5-2-1.9-2.5-4.5 1.4 0 2.9-.3 4.5-1a20 20 0 0 0 9-8.6l.5-1.2a48 48 0 0 1 12.6-.3Z"
      fill="#1834CE"
    />
    <path
      d="M38.7 63.5a9.4 9.4 0 0 1-1.2 2.4 20 20 0 0 1-8.4 7.3 8 8 0 0 1-4.4 1l-.6-4c1-1 3-2.8 6.4-4.3 2.1-1 4.8-1.9 8.2-2.4Z"
      fill="#3955F5"
    />
    <path
      d="M39.3 58.8c0 1.6-.1 3.2-.6 4.7-3.4.5-6 1.4-8.2 2.4A21 21 0 0 0 24 70a7.4 7.4 0 0 0-.9-3c-.2-.2-.3-.6-.4-1 .7-.8 2-1.9 4.2-3 2.6-1.5 6.6-3 12.3-4.3Z"
      fill="#fff"
    />
  </svg>
);
